import { useEffect, useRef, useState } from "react";

/**
 * XAES Agent plasma orbit. Ported from `_legacy/xaes-nav.js`
 * `window.mcAgentRunPlasma` — N quadratic curves radiating from the
 * canvas centre, each animated by independent phase + speed terms,
 * with a wide soft underglow stroke and a tight blue gradient stroke
 * on top.
 *
 * Theme-aware colour: resolves `--accent-true-blue` and `--accent-sky`
 * from `getComputedStyle(document.documentElement)` at mount and
 * re-resolves whenever the `<html>` `class` attribute changes (the
 * next-themes toggle). Same observer pattern as `SmokeCanvas`. The
 * vanilla hardcoded RGB triples for the gradient stops, which made
 * the plasma look correct only in dark mode.
 *
 * StrictMode-safe by construction:
 *   - `rafId` lives in a ref so the cleanup cancels deterministically.
 *   - `disposed` flag short-circuits the loop before it can schedule a
 *     follow-up frame after unmount.
 */

type Rgb = [number, number, number];

function parseHex(input: string): Rgb | null {
  const trimmed = input.trim();
  const m6 = trimmed.match(/^#([0-9a-fA-F]{6})$/);
  if (m6) {
    const hex = m6[1]!;
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ];
  }
  const m3 = trimmed.match(/^#([0-9a-fA-F]{3})$/);
  if (m3) {
    const hex = m3[1]!;
    return [
      parseInt(hex[0]! + hex[0]!, 16),
      parseInt(hex[1]! + hex[1]!, 16),
      parseInt(hex[2]! + hex[2]!, 16),
    ];
  }
  return null;
}

function readVar(name: string, fallback: Rgb): Rgb {
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
  return parseHex(raw) ?? fallback;
}

/**
 * Light a darker / brighter variant of an RGB triple by `factor`.
 * `factor > 1` brightens, `factor < 1` darkens. Clamped to 0-255.
 */
function shade([r, g, b]: Rgb, factor: number): Rgb {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v * factor)));
  return [clamp(r), clamp(g), clamp(b)];
}

type PlasmaCanvasProps = {
  className?: string;
  /** Inline style — useful for sizing (e.g. fixed `width`/`height`
   *  matching a specific legacy canvas like the WD chat welcome at
   *  120×66). */
  style?: React.CSSProperties;
  /** Number of orbiting nodes. The vanilla default is 10. */
  nodeCount?: number;
  /** Accessible label for screen readers. */
  ariaLabel?: string;
};

// Fallback if the CSS variables can't be resolved (e.g. SSR). Match the
// vanilla's hardcoded dark-mode True Blue / Sky family.
const FALLBACK_BLUE: Rgb = [73, 149, 255];
const FALLBACK_SKY: Rgb = [161, 230, 255];

export function PlasmaCanvas({
  className,
  style,
  nodeCount = 10,
  ariaLabel = "XAES Analyst plasma",
}: PlasmaCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Live-resolved palette: re-reads on theme toggle.
  const [palette, setPalette] = useState<{ blue: Rgb; sky: Rgb }>(() => ({
    blue: readVar("--accent-true-blue", FALLBACK_BLUE),
    sky: readVar("--accent-sky", FALLBACK_SKY),
  }));

  useEffect(() => {
    function reresolve() {
      setPalette({
        blue: readVar("--accent-true-blue", FALLBACK_BLUE),
        sky: readVar("--accent-sky", FALLBACK_SKY),
      });
    }
    reresolve();
    const observer = new MutationObserver(reresolve);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;

    type Node = {
      ax: number;
      ay: number;
      sx: number;
      sy: number;
      ox: number;
      oy: number;
    };

    const nodes: Node[] = [];
    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        ax: Math.random() * Math.PI * 2,
        ay: Math.random() * Math.PI * 2,
        sx: 0.3 + Math.random() * 0.5,
        sy: 0.3 + Math.random() * 0.5,
        ox: Math.random() * Math.PI * 2,
        oy: Math.random() * Math.PI * 2,
      });
    }

    // Pre-build the colour strings the loop will need. Three stops on
    // the inner stroke: bright (sky-leaning), mid (true-blue), deep
    // (true-blue darkened ~40 %). The underglow uses a slightly-shifted
    // bright cyan tuned to match the vanilla's `rgba(0, 180, 255, …)`.
    const [br, bg, bb] = palette.blue;
    const [sr, sg, sb] = palette.sky;
    const [dr, dg, db] = shade(palette.blue, 0.4);
    const underR = Math.round((br + sr) / 2);
    const underG = Math.round((bg + sg) / 2);
    const underB = Math.round((bb + sb) / 2);

    let rafId: number | null = null;
    let disposed = false;
    let t = 0;

    function resize() {
      if (!canvas || !ctx) return;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (!w || !h) return;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function draw() {
      if (disposed || !canvas || !ctx) return;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (!w || !h) {
        rafId = requestAnimationFrame(draw);
        return;
      }
      const cx = w / 2;
      const cy = h / 2;
      t += 0.012;
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < nodes.length; i++) {
        const nd = nodes[i]!;
        const tx = cx + w * 0.45 * Math.sin(nd.ax + t * nd.sx + nd.ox);
        const ty = cy + h * 0.45 * Math.cos(nd.ay + t * nd.sy + nd.oy);
        const mx =
          cx + (tx - cx) * 0.5 + Math.sin(t * 0.7 + i) * w * 0.08;
        const my =
          cy + (ty - cy) * 0.5 + Math.cos(t * 0.8 + i) * h * 0.08;
        const alpha = 0.55 + 0.35 * Math.sin(t * 1.2 + i * 0.9);
        const width = 0.6 + 0.8 * Math.abs(Math.sin(t * 0.6 + i));

        // Soft underglow — averaged sky+true-blue
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.quadraticCurveTo(mx, my, tx, ty);
        ctx.strokeStyle = `rgba(${underR},${underG},${underB},${(alpha * 0.25).toFixed(3)})`;
        ctx.lineWidth = width + 2.5;
        ctx.lineCap = "round";
        ctx.stroke();

        // Inner gradient stroke — bright sky → true-blue → darkened
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.quadraticCurveTo(mx, my, tx, ty);
        const grad = ctx.createLinearGradient(cx, cy, tx, ty);
        grad.addColorStop(0, `rgba(${sr},${sg},${sb},${(alpha * 0.95).toFixed(3)})`);
        grad.addColorStop(0.5, `rgba(${br},${bg},${bb},${(alpha * 0.7).toFixed(3)})`);
        grad.addColorStop(1, `rgba(${dr},${dg},${db},${(alpha * 0.2).toFixed(3)})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = width;
        ctx.stroke();
      }
      rafId = requestAnimationFrame(draw);
    }

    resize();
    window.addEventListener("resize", resize);
    rafId = requestAnimationFrame(draw);

    return () => {
      disposed = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
    };
  }, [nodeCount, palette]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={style}
      role="img"
      aria-label={ariaLabel}
    />
  );
}
